import * as Sentry from "@sentry/react"

// FE-P6 (Sentry blind-spot fix): identify who a crash hit, so issues can be segmented by
// role / tenant / team / affiliate. Grounded in the BaseUser model + its discriminators:
//   - ALL users (BaseUser): _id, role, company (tenant), contactGroups [ContactGroup ids]
//   - internal reps  (role 'user'):       populated subRole (e.g. supervisor)
//   - affiliates     (role 'affiliate'):  isGroupOwner / isAffiliateManager; the account
//                                         _id IS the affiliate id
//   - affiliateGroupsAdmin:               role only (visibleGroups exist but aren't needed here)
// A "department" is not a separate field — it's a ContactGroup with isDepartment=true. The
// sign-in response expands the ids to `contactGroupsInfo: [{id, name, isDepartment}]`
// (getAuthUser expandContactGroups), so we send readable `departments` / `groups` name
// lists. If that expanded info isn't present (e.g. a session from an older token/refresh),
// we fall back to the raw `contactGroups` ids.
export const setSentryUser = (user) => {
  if (!user) {
    Sentry.setUser(null)
    return
  }

  // subRole is populated for internal 'user' accounts (object with .value, e.g. 'supervisor');
  // tolerate a bare id/string defensively.
  const subRole =
    user.subRole && typeof user.subRole === "object" ? user.subRole.value : user.subRole

  // Readable department/group split from the expanded sign-in info, else fall back to ids.
  const info = Array.isArray(user.contactGroupsInfo) ? user.contactGroupsInfo : null
  const namesWhere = (predicate) =>
    info.filter(predicate).map((g) => g && g.name).filter(Boolean).join(",") || undefined
  const departments = info ? namesWhere((g) => g && g.isDepartment) : undefined
  const groups = info ? namesWhere((g) => g && !g.isDepartment) : undefined
  const contactGroupIds =
    !info && Array.isArray(user.contactGroups)
      ? user.contactGroups.filter(Boolean).join(",") || undefined
      : undefined

  Sentry.setUser({
    id: user._id || user.id,
    role: user.role,
    ...(subRole ? { subRole } : {}),
    ...(user.company ? { company: user.company } : {}),
    ...(departments ? { departments } : {}),
    ...(groups ? { groups } : {}),
    ...(contactGroupIds ? { contactGroups: contactGroupIds } : {}),
    // No affiliateId field exists on the user models: for an affiliate, the account id IS
    // the affiliate id — surface it explicitly so affiliate crashes are filterable by it.
    ...(user.role === "affiliate" ? { affiliateId: user._id || user.id } : {}),
    ...(user.isGroupOwner != null ? { isGroupOwner: user.isGroupOwner } : {}),
    ...(user.isAffiliateManager != null ? { isAffiliateManager: user.isAffiliateManager } : {}),
  })
}
